'use client';

import { c as _c } from "react/compiler-runtime";
import { useLenis } from 'lenis/react';
import { type MotionValue, motion, useMotionTemplate, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
type StartPosition = 'top' | 'center' | 'bottom';
function getCSSVariableOffset(): number {
  if (typeof window === 'undefined') return 0;
  const value = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height');
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}
function detectStickyOffset(): number {
  if (typeof window === 'undefined') return 0;
  let maxHeight = 0;
  const elements = document.querySelectorAll('*');
  for (const el of elements) {
    const style = getComputedStyle(el);
    const position = style.position;
    if (position === 'sticky' || position === 'fixed') {
      const rect = el.getBoundingClientRect();
      if (rect.top === 0 && rect.height > 0) {
        maxHeight = Math.max(maxHeight, rect.bottom);
      }
    }
  }
  return maxHeight;
}
function getTopOffset(explicitOffset?: number): number {
  if (explicitOffset !== undefined) return explicitOffset;
  const cssOffset = getCSSVariableOffset();
  if (cssOffset > 0) return cssOffset;
  return detectStickyOffset();
}
interface RevealContextValue {
  containerRef: React.RefObject<HTMLDivElement | null>;
  topHeight: number;
  setTopHeight: (height: number) => void;
  bottomHeight: number;
  setBottomHeight: (height: number) => void;
  startPosition: StartPosition;
  disabled: boolean;
  progress: MotionValue<number>;
  topOffset: number;
}
const RevealContext = createContext<RevealContextValue | null>(null);
const EffectContext = createContext<{
  isInsideEffect: boolean;
} | null>(null);
function useRevealContext() {
  const context = useContext(RevealContext);
  if (!context) {
    throw new Error("RevealTop and RevealBottom must be used within Reveal");
  }
  return context;
}
function RevealTop(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const topRef = useRef(null);
  const {
    setTopHeight
  } = useRevealContext();
  let t1;
  let t2;
  if ($[4] !== setTopHeight) {
    t1 = () => {
      const updateHeight = () => {
        if (topRef.current) {
          const height = topRef.current.offsetHeight;
          if (height > 0) {
            setTopHeight(height);
          }
        }
      };
      updateHeight();
      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateHeight);
      });
      if (topRef.current) {
        resizeObserver.observe(topRef.current);
      }
      return () => resizeObserver.disconnect();
    };
    t2 = [setTopHeight];
    $[4] = setTopHeight;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== className) {
    t3 = cn("relative z-30 w-full", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t3) {
    t4 = <div ref={topRef} className={t3} {...props}>{children}</div>;
    $[9] = children;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function RevealBottom(t0) {
  const $ = _c(29);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    disabled,
    topHeight,
    topOffset,
    setBottomHeight
  } = useRevealContext();
  const effectContext = useContext(EffectContext);
  const isInsideEffect = effectContext?.isInsideEffect ?? false;
  const bottomRef = useRef(null);
  let t1;
  let t2;
  if ($[4] !== setBottomHeight) {
    t1 = () => {
      const updateHeight = () => {
        if (bottomRef.current) {
          const height = bottomRef.current.offsetHeight;
          if (height > 0) {
            setBottomHeight(height);
          }
        }
      };
      updateHeight();
      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateHeight);
      });
      if (bottomRef.current) {
        resizeObserver.observe(bottomRef.current);
      }
      return () => resizeObserver.disconnect();
    };
    t2 = [setBottomHeight];
    $[4] = setBottomHeight;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  if (disabled) {
    let t3;
    if ($[7] !== className) {
      t3 = cn("absolute top-0 left-0 z-10 w-full", className);
      $[7] = className;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== children || $[10] !== props || $[11] !== t3) {
      t4 = <div ref={bottomRef} className={t3} {...props}>{children}</div>;
      $[9] = children;
      $[10] = props;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (isInsideEffect) {
    let t3;
    if ($[13] !== className) {
      t3 = cn("w-full", className);
      $[13] = className;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== children || $[16] !== props || $[17] !== t3) {
      t4 = <div ref={bottomRef} className={t3} {...props}>{children}</div>;
      $[15] = children;
      $[16] = props;
      $[17] = t3;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  let t3;
  if ($[19] !== className) {
    t3 = cn("sticky z-10 w-full", className);
    $[19] = className;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const t4 = `${topOffset}px`;
  const t5 = topHeight > 0 ? `-${topHeight}px` : undefined;
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = {
      top: t4,
      marginTop: t5
    };
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== children || $[25] !== props || $[26] !== t3 || $[27] !== t6) {
    t7 = <div ref={bottomRef} className={t3} style={t6} {...props}>{children}</div>;
    $[24] = children;
    $[25] = props;
    $[26] = t3;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}
type EffectType = 'fadeIn' | 'blurIn' | 'fadeOutBlack' | 'scaleIn';
interface RevealBottomEffectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'> {
  effect: EffectType;
  intensity?: number;
}
function RevealBottomEffect({
  children,
  className,
  effect,
  intensity = 1,
  ...props
}: RevealBottomEffectProps) {
  const {
    progress,
    disabled,
    topHeight,
    bottomHeight,
    topOffset
  } = useRevealContext();
  const shouldReduceMotion = useReducedMotion();
  const fullRevealP = topHeight > 0 && bottomHeight > 0 ? topHeight / (topHeight + bottomHeight) : 0.5;
  const maxBlur = 20 * intensity;
  const minScale = 1 - 0.2 * intensity;
  const fadeInOpacity = useTransform(progress, [0, fullRevealP, 1], [0, 1, 1]);
  const filter = useTransform(progress, [0, fullRevealP, 1], [`blur(${maxBlur}px)`, 'blur(0px)', 'blur(0px)']);
  const filterTemplate = useMotionTemplate` ${filter} `;
  const overlayOpacity = useTransform(progress, [0, fullRevealP, 1], [intensity, 0, 0]);
  const scale = useTransform(progress, [0, fullRevealP, 1], [minScale, 1, 1]);
  const stickyStyle = {
    top: `${topOffset}px`,
    marginTop: topHeight > 0 ? `-${topHeight}px` : undefined
  };
  if (shouldReduceMotion || disabled) {
    return <EffectContext.Provider value={{
      isInsideEffect: true
    }}>
        <div className={cn('absolute top-0 left-0 z-10 w-full', className)} {...props}>
          {children}
        </div>
      </EffectContext.Provider>;
  }
  return <EffectContext.Provider value={{
    isInsideEffect: true
  }}>
      {(() => {
      switch (effect) {
        case 'fadeIn':
          {
            return <motion.div className={cn('sticky z-10 w-full', className)} style={{
              ...stickyStyle,
              opacity: fadeInOpacity
            }} {...props}>
                {children}
              </motion.div>;
          }
        case 'blurIn':
          {
            return <motion.div className={cn('sticky z-10 w-full', className)} style={{
              ...stickyStyle,
              filter: filterTemplate
            }} {...props}>
                {children}
              </motion.div>;
          }
        case 'fadeOutBlack':
          {
            return <div className={cn('sticky z-10 w-full', className)} style={stickyStyle} {...props}>
                <motion.div className='pointer-events-none absolute inset-0 z-20 bg-black' style={{
                opacity: overlayOpacity
              }} />
                {children}
              </div>;
          }
        case 'scaleIn':
          {
            return <motion.div className={cn('sticky z-10 w-full', className)} style={{
              ...stickyStyle,
              scale
            }} {...props}>
                {children}
              </motion.div>;
          }
      }
    })()}
    </EffectContext.Provider>;
}
interface RevealProps extends React.HTMLAttributes<HTMLDivElement> {
  startPosition?: StartPosition;
  disabled?: boolean;
  topOffset?: number;
}
function Reveal(t0) {
  const $ = _c(38);
  let children;
  let className;
  let explicitTopOffset;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      className,
      startPosition: t1,
      disabled: t2,
      topOffset: explicitTopOffset,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = explicitTopOffset;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    explicitTopOffset = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const startPosition = t1 === undefined ? "top" : t1;
  const disabled = t2 === undefined ? false : t2;
  const containerRef = useRef(null);
  const [topHeight, setTopHeight] = useState(0);
  const [bottomHeight, setBottomHeight] = useState(0);
  const progress = useMotionValue(0);
  const [topOffset, setTopOffset] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const effectiveDisabled = disabled || shouldReduceMotion || false;
  const lenis = useLenis();
  let t3;
  let t4;
  if ($[7] !== explicitTopOffset) {
    t3 = () => {
      setTopOffset(getTopOffset(explicitTopOffset));
    };
    t4 = [explicitTopOffset];
    $[7] = explicitTopOffset;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== effectiveDisabled || $[11] !== lenis || $[12] !== progress || $[13] !== startPosition || $[14] !== topHeight || $[15] !== topOffset) {
    t5 = () => {
      if (effectiveDisabled || !lenis || topHeight === 0) {
        return;
      }
      const updateProgress = () => {
        const container = containerRef.current;
        if (!container) {
          return;
        }
        const rect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const sectionTop = rect.top - topOffset;
        const sectionHeight = rect.height;
        let progressValue = 0;
        bb34: switch (startPosition) {
          case "top":
            {
              progressValue = -sectionTop / sectionHeight;
              break bb34;
            }
          case "center":
            {
              const startPoint_0 = viewportHeight * 0.5;
              const traveled_0 = startPoint_0 - sectionTop;
              progressValue = traveled_0 / sectionHeight;
              break bb34;
            }
          case "bottom":
            {
              const startPoint = viewportHeight;
              const traveled = startPoint - sectionTop;
              progressValue = traveled / sectionHeight;
            }
        }
        progress.set(Math.max(0, Math.min(1, progressValue)));
      };
      updateProgress();
      lenis.on("scroll", updateProgress);
      return () => lenis.off("scroll", updateProgress);
    };
    t6 = [lenis, progress, startPosition, effectiveDisabled, containerRef, topHeight, topOffset];
    $[10] = effectiveDisabled;
    $[11] = lenis;
    $[12] = progress;
    $[13] = startPosition;
    $[14] = topHeight;
    $[15] = topOffset;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== bottomHeight || $[19] !== effectiveDisabled || $[20] !== progress || $[21] !== startPosition || $[22] !== topHeight || $[23] !== topOffset) {
    t7 = {
      containerRef,
      topHeight,
      setTopHeight,
      bottomHeight,
      setBottomHeight,
      startPosition,
      disabled: effectiveDisabled,
      progress,
      topOffset
    };
    $[18] = bottomHeight;
    $[19] = effectiveDisabled;
    $[20] = progress;
    $[21] = startPosition;
    $[22] = topHeight;
    $[23] = topOffset;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== className) {
    t8 = cn("relative", className);
    $[25] = className;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = topHeight > 0 && bottomHeight > 0 && !effectiveDisabled ? topHeight + bottomHeight : undefined;
  const t10 = topHeight > 0 && bottomHeight > 0 ? undefined : "100vh";
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = {
      height: t9,
      minHeight: t10
    };
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== children || $[31] !== props || $[32] !== t11 || $[33] !== t8) {
    t12 = <div ref={containerRef} className={t8} style={t11} {...props}>{children}</div>;
    $[30] = children;
    $[31] = props;
    $[32] = t11;
    $[33] = t8;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12 || $[36] !== t7) {
    t13 = <RevealContext.Provider value={t7}>{t12}</RevealContext.Provider>;
    $[35] = t12;
    $[36] = t7;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}
export { Reveal, RevealBottom, RevealBottomEffect, RevealTop };